/**
 * Fornitalia stores dates as `dd/mm/yyyy` strings for backwards compat
 * with the PowerApps conventions. These helpers bridge that format with
 * native `Date` objects (needed for date-range comparisons) and with the
 * HTML `<input type="date">` value format (`yyyy-mm-dd`).
 *
 * Keep the parser strict: trust SharePoint's own writes, but refuse
 * malformed input rather than silently yielding wrong dates.
 */

/** Parse a `dd/mm/yyyy` string into a local `Date`, or null if invalid. */
export function parseAr(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (
    d.getFullYear() !== Number(yyyy) ||
    d.getMonth() !== Number(mm) - 1 ||
    d.getDate() !== Number(dd)
  ) {
    return null;
  }
  return d;
}

/** Format a `Date` as `dd/mm/yyyy` in local time. */
export function formatAr(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** `yyyy-mm-dd` (HTML date-input value) → local `Date`. */
export function parseIso(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, yyyy, mm, dd] = m;
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
}

/**
 * Returns true when `date` is inside the [desde, hasta] inclusive range.
 * Either endpoint can be null (meaning "no lower / upper bound").
 */
export function inRange(date: Date, desde: Date | null, hasta: Date | null): boolean {
  if (desde && date < stripTime(desde)) return false;
  if (hasta && date > endOfDay(hasta)) return false;
  return true;
}

function stripTime(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}
